/**
 * The release sitting (13-12, ISS-167). Oversight's bulk table of unreleased Action Register rows.
 * Built to the approved mock (design/mock-13, Release tab; D-13-02-1).
 *
 *   - Filters: Source and batch chips (`facetCounts`, `clusterHint`), Kind, Owner role, Done by
 *     (`Executor`), a text search and an age slider. Select-all means the FILTERED set
 *     (`resolveSelection`), never every row.
 *   - Unreleased rows that can't be released (ISS-187: Done rows go to Verify) are shown at the end
 *     of the table with their reason, never hidden, and can't be selected.
 *   - One shared note, required for both verbs: it goes into each owner's card word for word.
 *   - Release / Retire sit behind a confirm (no toast-undo for bulk), then run as sequential
 *     `$batch` chunks of 20 with If-Match through `getWriter()` (practice mode in dev: nothing is
 *     saved). Per-chunk progress, then a result line that names every exception; conflicts and
 *     failures stay selected with a reason; "Retry the N" re-runs only those, after Retry-After.
 *   - The table refetches after a run, so released rows leave the queue.
 *
 * Performance (13-11's measured pattern): a memoised row with ONE stable handler object, typed
 * text kept in its own component, and at most 50 rows on screen ("Show the next 50").
 *
 * `ReleasePage` wires the runtime into `ReleaseBoard`, which takes a `ReleaseSource` so it can be
 * viewed with synthetic data (design/release-preview).
 */

import { memo, useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  Modal,
  ModalOverlay,
  Row,
  Table,
  TableBody,
  TableHeader,
} from 'react-aria-components';
import type { Key, Selection } from 'react-aria-components';
import { useActionRuntime } from '../../hooks/useActionRuntime';
import { useActor } from '../../hooks/useActor';
import { usePublishNavCount } from '../../hooks/useNavCounts';
import { listActionRows } from '../../services/actionService';
import type { BulkProgress, BulkSummary } from '../../services/actionService';
import type { BulkOpts, PracticeSimulation, WriteMode } from '../../services/commitAdapter';
import { isRegulatoryDecision } from '../../lib/actionAuthority';
import {
  ageDays,
  applyFilters,
  clusterHint,
  facetCounts,
  notReleasable,
  parseSimulateFlag,
  pastDue,
  releaseQueue,
  resolveSelection,
  rowKey,
} from '../../lib/releaseFilters';
import type { ReleaseFilters } from '../../lib/releaseFilters';
import type { ActionKind, ActionRow, ActionSource, Actor, Executor, OwnerRole } from '../../types/actionRegister';
import { ReleaseConfirm, ReleaseProgress, ReleaseResult } from './ReleaseConfirm';
import type { SittingVerb } from './ReleaseConfirm';
import styles from './ReleasePage.module.css';

// ---------------------------------------------------------------------------
// The data source (Graph + writer in the app; synthetic in the preview)
// ---------------------------------------------------------------------------

export interface ReleaseSource {
  mode: WriteMode;
  /** Every Action Register row (paged through nextLink); the page builds the queue. */
  listRows: () => Promise<ActionRow[]>;
  run: (verb: SittingVerb, rows: readonly ActionRow[], note: string, opts: BulkOpts) => Promise<BulkSummary>;
  retryAudit: (sittingId: string, pending: BulkSummary['auditPending']) => Promise<BulkSummary['auditPending']>;
}

export default function ReleasePage() {
  const { io, writer, siteError } = useActionRuntime();
  const actorState = useActor();
  const actor = actorState.status === 'ready' ? actorState.actor : null;

  const source = useMemo<ReleaseSource | null>(() => {
    if (!io || !actor) return null;
    return {
      mode: writer.mode,
      listRows: () => listActionRows(io),
      run: (verb, rows, note, opts) =>
        verb === 'release' ? writer.releaseBatch(io, rows, note, actor, opts) : writer.retireBatch(io, rows, note, actor, opts),
      retryAudit: (sittingId, pending) => writer.retryBulkAudit(io, sittingId, pending),
    };
  }, [io, actor, writer]);

  // Dev-only: `?simulate=412,429` shows the conflict / retry path in the PRACTICE sitting.
  const simulate = useMemo<PracticeSimulation | null>(() => {
    if (!import.meta.env.DEV || writer.mode !== 'practice') return null;
    return parseSimulateFlag(new URLSearchParams(window.location.search).get('simulate'));
  }, [writer.mode]);

  return <ReleaseBoard source={source} actor={actor} problem={siteError} simulate={simulate} />;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const DUE = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', day: 'numeric', month: 'short' });
const LONG = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function fmt(f: Intl.DateTimeFormat, iso: string | null): string {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? '' : f.format(t);
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const ROLE_LABEL: Record<OwnerRole, string> = {
  Oversight: 'Oversight',
  CareManager: 'Care Manager',
  HRWHS: 'HR & WHS',
  Finance: 'Finance',
  Admin: 'Admin',
  Board: 'Board',
  QCAC: 'QCAC',
  Wellness: 'Wellness',
  DAG: 'DAG',
};

// ---------------------------------------------------------------------------
// State shapes
// ---------------------------------------------------------------------------

interface Base {
  token: number;
  loadedAt: number;
  rows: ActionRow[];
}

type LoadState = { token: number; status: 'ready'; base: Base } | { token: number; status: 'error'; message: string };

interface UiFilters {
  source: ActionSource | '';
  kind: ActionKind | '';
  ownerRole: OwnerRole | '';
  executor: Executor | '';
  olderThanDays: number;
  cluster: string;
}

const DEFAULT_FILTERS: UiFilters = { source: '', kind: '', ownerRole: '', executor: '', olderThanDays: 0, cluster: '' };

type BulkState =
  | null
  | { phase: 'confirm'; verb: SittingVerb; rows: ActionRow[]; note: string; at: number }
  | { phase: 'running'; verb: SittingVerb; count: number; progress: BulkProgress | null }
  | {
      phase: 'done';
      verb: SittingVerb;
      note: string;
      summary: BulkSummary;
      asked: number;
      retryAt: number | null;
      busy: 'retry' | 'audit' | null;
    };

/** Rows on screen at a time: React Aria's Table re-renders every row on a selection change. */
const PAGE_SIZE = 50;
/** Age in days past which the column turns a gentle amber (the mock). */
const OLD_DAYS = 60;

const detailKey = (id: number) => `d:${id}`;

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

interface BoardProps {
  source: ReleaseSource | null;
  actor: Actor | null;
  problem?: string | null;
  /** Dev-only practice simulation, passed to the first run only (never to a retry). */
  simulate?: PracticeSimulation | null;
}

export function ReleaseBoard({ source, actor, problem = null, simulate = null }: BoardProps) {
  const [reloadToken, setReloadToken] = useState(0);
  const [load, setLoad] = useState<LoadState | null>(null);
  const [patches, setPatches] = useState<{ token: number; map: ReadonlyMap<number, Partial<ActionRow>> }>({
    token: -1,
    map: new Map(),
  });
  const [filters, setFilters] = useState<UiFilters>(DEFAULT_FILTERS);
  const [text, setText] = useState('');
  const deferredText = useDeferredValue(text);
  const [selected, setSelected] = useState<Selection>(new Set<Key>());
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [marks, setMarks] = useState<ReadonlyMap<number, string>>(new Map());
  const [bulk, setBulk] = useState<BulkState>(null);
  const [anchor, setAnchor] = useState<number | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [noteKey, setNoteKey] = useState(0);

  // ---- reads -------------------------------------------------------------

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    const token = reloadToken;
    source
      .listRows()
      .then((rows) => {
        if (!cancelled) setLoad({ token, status: 'ready', base: { token, loadedAt: Date.now(), rows } });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoad({ token, status: 'error', message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  const base = load?.status === 'ready' && load.token === reloadToken ? load.base : null;
  const shown = load?.status === 'ready' ? load.base : null; // the last good read, shown while reloading
  const now = shown?.loadedAt ?? 0;
  const practice = source?.mode === 'practice';

  /**
   * Rows as they are now. A run's applied rows are patched locally until the refetch lands; in
   * practice mode nothing was written, so the patch is kept (otherwise "released" rows would come
   * straight back), and the page says so.
   */
  const all = useMemo<ActionRow[]>(() => {
    if (!shown) return [];
    const keep = practice || patches.token === shown.token;
    if (!keep || patches.map.size === 0) return shown.rows;
    return shown.rows.map((r) => (patches.map.has(r.id) ? { ...r, ...patches.map.get(r.id) } : r));
  }, [shown, patches, practice]);

  const overlay = useCallback(
    (rows: ActionRow[]) => rows.map((r) => (patches.map.has(r.id) ? { ...r, ...patches.map.get(r.id) } : r)),
    [patches],
  );

  // ---- derived -----------------------------------------------------------

  const queue = useMemo(() => {
    if (!actor) return [];
    const q = releaseQueue(all, actor);
    return q.sort((a, b) => (Date.parse(a.created) || 0) - (Date.parse(b.created) || 0) || a.id - b.id);
  }, [all, actor]);

  const held = useMemo(() => notReleasable(all), [all]);
  const heldReason = useMemo(() => new Map(held.map((h) => [h.row.id, h.reason] as const)), [held]);

  usePublishNavCount('/release', shown && actor ? queue.length : undefined);

  const facets = useMemo(() => facetCounts(queue), [queue]);
  const clusters = useMemo(() => clusterHint(queue).slice(0, 8), [queue]);
  const maxAge = useMemo(() => Math.max(30, ...queue.map((r) => ageDays(r, now) ?? 0)), [queue, now]);

  const libFilters = useMemo<ReleaseFilters>(() => ({ ...filters, text: deferredText }), [filters, deferredText]);
  const filteredQueue = useMemo(() => applyFilters(queue, libFilters, now), [queue, libFilters, now]);
  const filteredHeld = useMemo(
    () => applyFilters(held.map((h) => h.row), libFilters, now),
    [held, libFilters, now],
  );
  const visible = useMemo(() => [...filteredQueue, ...filteredHeld], [filteredQueue, filteredHeld]);
  const onScreen = useMemo(() => visible.slice(0, limit), [visible, limit]);

  const busyRun = bulk?.phase === 'running';
  const selectedIds = useMemo(() => resolveSelection(selected, filteredQueue), [selected, filteredQueue]);
  const selectedRows = useMemo(() => {
    const ids = new Set(selectedIds);
    return filteredQueue.filter((r) => ids.has(r.id));
  }, [selectedIds, filteredQueue]);

  const disabledKeys = useMemo(() => {
    const keys: Key[] = [];
    for (const r of onScreen) {
      keys.push(detailKey(r.id));
      if (heldReason.has(r.id) || busyRun) keys.push(rowKey(r.id));
    }
    return keys;
  }, [onScreen, heldReason, busyRun]);

  const regulatory = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of visible) {
      const c = isRegulatoryDecision(r);
      if (c.regulatory) m.set(r.id, c.because);
    }
    return m;
  }, [visible]);

  // ---- the sitting -------------------------------------------------------

  const applyLocal = (verb: SittingVerb, ids: readonly number[], note: string) => {
    if (!shown || ids.length === 0) return;
    const at = new Date().toISOString();
    setPatches((prev) => {
      const map = new Map(practice || prev.token === shown.token ? prev.map : []);
      for (const id of ids) {
        map.set(
          id,
          verb === 'release'
            ? { releasedAt: at, releasedBy: actor?.upn ?? null, releaseNote: note }
            : { status: 'Retired', releaseNote: note },
        );
      }
      return { token: shown.token, map };
    });
  };

  /**
   * `carry`: on a retry, the earlier run's other exceptions (its conflicts and refusals), which stay
   * selected with their reason; the retry re-runs only the failed rows.
   */
  const run = async (
    verb: SittingVerb,
    rows: ActionRow[],
    note: string,
    sim: PracticeSimulation | null,
    carry: ReadonlyMap<number, string> = new Map(),
  ) => {
    if (!source) return;
    setMarks(new Map(carry));
    setBulk({ phase: 'running', verb, count: rows.length, progress: null });
    let summary: BulkSummary;
    try {
      summary = await source.run(verb, rows, note, {
        onProgress: (progress) => setBulk((b) => (b?.phase === 'running' ? { ...b, progress } : b)),
        ...(sim ? { simulate: sim } : {}),
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      summary = {
        verb,
        sittingId: '',
        applied: [],
        conflicts: [],
        retried: 0,
        failed: rows.map((r) => ({ id: r.id, status: null, reason })),
        refused: [],
        auditPending: [],
        eTags: {},
      };
    }
    applyLocal(verb, summary.applied, note);
    const next = new Map<number, string>(carry);
    for (const id of summary.conflicts) next.set(id, 'Changed by someone else, reloaded');
    for (const f of summary.failed) next.set(f.id, f.reason === 'throttled' ? 'SharePoint asked us to wait, retry' : "Didn't save, retry");
    for (const f of summary.refused) next.set(f.id, 'Not sent');
    setMarks(next);
    setSelected(new Set<Key>([...next.keys()].map(rowKey)));
    if (next.size === 0) setNoteKey((k) => k + 1); // everything went: the note starts empty again
    setBulk({
      phase: 'done',
      verb,
      note,
      summary,
      asked: rows.length,
      retryAt: summary.failed.length && summary.retryAfterMs ? Date.now() + summary.retryAfterMs : null,
      busy: null,
    });
    reload();
  };

  const retryFailed = async () => {
    if (bulk?.phase !== 'done' || !source || !actor) return;
    const b = bulk;
    setBulk({ ...b, busy: 'retry' });
    let fresh: ActionRow[];
    try {
      fresh = overlay(await source.listRows());
    } catch {
      setBulk({ ...b, busy: null });
      return;
    }
    const ids = new Set(b.summary.failed.map((f) => f.id));
    // Only rows still waiting: one that landed after a lost reply has already left the queue.
    const rows = releaseQueue(fresh, actor).filter((r) => ids.has(r.id));
    const carry = new Map([...marks].filter(([id]) => !ids.has(id)));
    await run(b.verb, rows, b.note, null, carry);
  };

  const postAudit = async () => {
    if (bulk?.phase !== 'done' || !source) return;
    const b = bulk;
    setBulk({ ...b, busy: 'audit' });
    let still = b.summary.auditPending;
    try {
      still = await source.retryAudit(b.summary.sittingId, b.summary.auditPending);
    } catch {
      // keep them pending; the button stays
    }
    setBulk({ ...b, busy: null, summary: { ...b.summary, auditPending: still } });
  };

  const openConfirm = (verb: SittingVerb, note: string) => {
    if (selectedRows.length === 0 || !note.trim()) return;
    setBulk({ phase: 'confirm', verb, rows: selectedRows, note: note.trim(), at: Date.now() });
  };

  /** A plain click on a box sets the range anchor; a Shift-click sets every selectable row between. */
  const onCheckboxClick = (e: ReactMouseEvent, id: number) => {
    if (!e.shiftKey || anchor === null || anchor === id) {
      setAnchor(id);
      return;
    }
    const order = filteredQueue.map((r) => r.id);
    const a = order.indexOf(anchor);
    const z = order.indexOf(id);
    if (a < 0 || z < 0) {
      setAnchor(id);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const on = !selectedIds.includes(id);
    const next = new Set<Key>(selectedIds.map(rowKey));
    for (const rid of order.slice(Math.min(a, z), Math.max(a, z) + 1)) {
      if (on) next.add(rowKey(rid));
      else next.delete(rowKey(rid));
    }
    setSelected(next);
    setAnchor(id);
  };

  // Row handlers: ONE stable object, so a memoised row re-renders only when its own props change.
  const latest = useRef<RowHandlers | null>(null);
  useLayoutEffect(() => {
    latest.current = {
      toggleExpand: (id) =>
        setExpanded((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        }),
      checkboxClick: onCheckboxClick,
    };
  });
  const handlers = useMemo<RowHandlers>(
    () => ({
      toggleExpand: (id) => latest.current?.toggleExpand(id),
      checkboxClick: (e, id) => latest.current?.checkboxClick(e, id),
    }),
    [],
  );

  // ---- render ------------------------------------------------------------

  const head = (side: ReactNode) => (
    <div className={styles.head}>
      <div>
        <h1 className={styles.title}>Release sitting</h1>
        <p className={styles.lede}>
          Nobody is nudged about a row until it is released. Filter, pick a batch, write one note for all of it, then
          release or retire.
        </p>
      </div>
      <div className={styles.side}>{side}</div>
    </div>
  );

  if (problem) {
    return (
      <div className={styles.page}>
        {head(null)}
        <div className={styles.notice}>
          <h2>The Q&amp;S site can't be reached.</h2>
          <p>{problem}. Reload the page to try again.</p>
        </div>
      </div>
    );
  }

  if (actor && !actor.roles.includes('Oversight')) {
    return (
      <div className={styles.page}>
        {head(null)}
        <div className={styles.notice}>
          <h2>The release sitting is Oversight's.</h2>
          <p>When a row is released to your role it appears on Today.</p>
        </div>
      </div>
    );
  }

  if (!shown) {
    const failed = load?.status === 'error' ? load.message : null;
    return (
      <div className={styles.page}>
        {head(null)}
        <div className={styles.tsheet}>
          <div className={styles.empty}>
            {failed ? (
              <>
                <h2>The Action Register couldn't be read.</h2>
                <p>{failed}</p>
                <Button className={`${styles.btn} ${styles.line}`} onPress={reload}>
                  Try again
                </Button>
              </>
            ) : (
              <p>Loading the Action Register…</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const setFilter = <K extends keyof UiFilters>(k: K, v: UiFilters[K]) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setSelected(new Set<Key>());
    setAnchor(null);
    setLimit(PAGE_SIZE);
  };
  const filtered = text.trim() !== '' || (Object.keys(DEFAULT_FILTERS) as Array<keyof UiFilters>).some((k) => filters[k] !== DEFAULT_FILTERS[k]);
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setText('');
    setSelected(new Set<Key>());
    setLimit(PAGE_SIZE);
  };

  const select = <K extends 'kind' | 'ownerRole' | 'executor'>(
    k: K,
    label: string,
    any: string,
    values: ReadonlyArray<{ value: string; count: number }>,
    name: (v: string) => string = (v) => v,
  ) => (
    <label className={styles.filter}>
      {label}
      <select value={filters[k]} onChange={(e) => setFilter(k, e.target.value as UiFilters[K])}>
        <option value="">{any}</option>
        {values.map((f) => (
          <option key={f.value} value={f.value}>
            {name(f.value)} ({f.count})
          </option>
        ))}
      </select>
    </label>
  );

  const allSelected = filteredQueue.length > 0 && selectedIds.length === filteredQueue.length;
  const lateInSelection = pastDue(selectedRows, now).length;

  return (
    <div className={styles.page}>
      {head(
        <>
          <b>{queue.length}</b>waiting to be released
          {held.length ? <span className={styles.sideNote}>{plural(held.length, 'more row')} can't be released</span> : null}
        </>,
      )}

      <div className={styles.chips} role="group" aria-label="Source">
        <span className={styles.chipsLabel}>Source</span>
        <button
          type="button"
          className={styles.chip}
          aria-pressed={filters.source === ''}
          onClick={() => setFilter('source', '')}
        >
          All <b>{queue.length}</b>
        </button>
        {facets.source.map((f) => (
          <button
            key={f.value}
            type="button"
            className={styles.chip}
            aria-pressed={filters.source === f.value}
            onClick={() => setFilter('source', filters.source === f.value ? '' : f.value)}
          >
            {f.value} <b>{f.count}</b>
          </button>
        ))}
      </div>
      {clusters.length ? (
        <div className={styles.chips} role="group" aria-label="Batches that arrived together">
          <span className={styles.chipsLabel}>Batches</span>
          {clusters.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`${styles.chip} ${styles.cluster}`}
              aria-pressed={filters.cluster === c.key}
              title={`${c.count} ${c.source} rows whose parent starts ${c.prefix}`}
              onClick={() => setFilter('cluster', filters.cluster === c.key ? '' : c.key)}
            >
              {c.prefix} <span className={styles.chipSub}>{c.source}, from {fmt(DUE, c.firstCreated)}</span> <b>{c.count}</b>
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.filters} role="group" aria-label="Filters">
        {select('kind', 'Kind', 'All kinds', facets.kind)}
        {select('ownerRole', 'Owner role', 'Any role', facets.ownerRole, (v) => ROLE_LABEL[v as OwnerRole] ?? v)}
        {select('executor', 'Done by', 'People or agents', facets.executor, (v) => (v === 'Agent' ? 'An agent' : 'A person'))}
        <label className={`${styles.filter} ${styles.search}`}>
          Search
          <input
            type="search"
            value={text}
            placeholder="Title, ACT ref, source ref"
            onChange={(e) => {
              setText(e.target.value);
              setSelected(new Set<Key>());
              setLimit(PAGE_SIZE);
            }}
          />
        </label>
        <label className={`${styles.filter} ${styles.age}`}>
          <span>
            Age: <output>{filters.olderThanDays ? `older than ${filters.olderThanDays} days` : 'any'}</output>
          </span>
          <input
            type="range"
            min={0}
            max={maxAge}
            step={1}
            value={Math.min(filters.olderThanDays, maxAge)}
            aria-valuetext={filters.olderThanDays ? `Older than ${filters.olderThanDays} days` : 'Any age'}
            onChange={(e) => setFilter('olderThanDays', Number(e.target.value))}
          />
        </label>
        <p className={styles.showing} aria-live="polite">
          {filtered ? `Showing ${filteredQueue.length} of ${queue.length}` : `${queue.length} rows`}
          {filteredHeld.length ? `, and ${filteredHeld.length} at the end that can't be released` : ''}
          {filtered ? (
            <>
              {' '}
              <button type="button" className={styles.linkbtn} onClick={clearFilters}>
                Clear
              </button>
            </>
          ) : null}
          {base ? null : ', reloading…'}
        </p>
      </div>

      {practice && patches.map.size > 0 ? (
        <p className={styles.practiceNote}>
          Practice mode: {plural(patches.map.size, 'row')} you released or retired {patches.map.size === 1 ? 'is' : 'are'} hidden
          here, but nothing was saved. Reload the page to see them again.
        </p>
      ) : null}

      <div className={styles.tsheet}>
        <Table
          aria-label="Unreleased Action Register rows"
          className={styles.table}
          selectionMode="multiple"
          selectionBehavior="toggle"
          selectedKeys={selected}
          onSelectionChange={(keys) => setSelected(keys === 'all' ? new Set(filteredQueue.map((r) => rowKey(r.id))) : keys)}
          disabledKeys={disabledKeys}
          disabledBehavior="selection"
        >
          <TableHeader>
            <Column className={styles.ck}>
              <Checkbox
                slot="selection"
                className={styles.check}
                aria-label={`Select all ${filteredQueue.length} rows that match the filters, including any not yet shown`}
              >
                {({ isSelected, isIndeterminate }) => <Box on={isSelected || allSelected} mixed={isIndeterminate} />}
              </Checkbox>
            </Column>
            <Column className={styles.colRef}>Row</Column>
            <Column isRowHeader className={styles.colTitle}>
              Title
            </Column>
            <Column className={styles.colKind}>Kind</Column>
            <Column className={styles.colSource}>Source</Column>
            <Column className={styles.colRole}>Owner role</Column>
            <Column className={styles.colDue}>Due</Column>
            <Column className={`${styles.num} ${styles.colAge}`}>
              <span title="Days since the row was created">Age</span>
            </Column>
            <Column className={styles.colBy}>Done by</Column>
            <Column className={styles.acts}>
              <span className={styles.sr}>Details</span>
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <div className={styles.empty}>
                {filtered ? (
                  <>
                    <h2>No rows match.</h2>
                    <p>Loosen a filter to see more.</p>
                    <Button className={`${styles.btn} ${styles.line}`} onPress={clearFilters}>
                      Clear the filters
                    </Button>
                  </>
                ) : (
                  <>
                    <h2>Nothing is waiting.</h2>
                    <p>Every open row has been released to its owner.</p>
                  </>
                )}
              </div>
            )}
          >
            {onScreen.map((r) => (
              <ReleaseRow
                key={r.id}
                row={r}
                held={heldReason.get(r.id) ?? null}
                regulatory={regulatory.get(r.id) ?? null}
                mark={marks.get(r.id) ?? null}
                now={now}
                expanded={expanded.has(r.id)}
                on={handlers}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {visible.length > onScreen.length ? (
        <div className={styles.more}>
          <Button className={`${styles.btn} ${styles.line}`} onPress={() => setLimit((n) => n + PAGE_SIZE)}>
            Show the next {Math.min(PAGE_SIZE, visible.length - onScreen.length)}
          </Button>
          <span>
            {onScreen.length} of {visible.length} shown, oldest first
          </span>
        </div>
      ) : null}

      <ActionBar
        key={noteKey}
        count={selectedIds.length}
        late={lateInSelection}
        disabled={busyRun}
        onClear={() => setSelected(new Set<Key>())}
        onRelease={(note) => openConfirm('release', note)}
        onRetire={(note) => openConfirm('retire', note)}
      />

      <ModalOverlay
        className={styles.overlay}
        isOpen={bulk !== null}
        isDismissable={bulk?.phase === 'confirm' || (bulk?.phase === 'done' && bulk.busy === null)}
        isKeyboardDismissDisabled={bulk?.phase === 'running' || (bulk?.phase === 'done' && bulk.busy !== null)}
        onOpenChange={(isOpen) => {
          if (!isOpen && bulk?.phase !== 'running') setBulk(null);
        }}
      >
        <Modal className={styles.modal}>
          <Dialog className={styles.dialog} role={bulk?.phase === 'confirm' ? 'alertdialog' : 'dialog'}>
            {bulk?.phase === 'confirm' ? (
              <ReleaseConfirm
                verb={bulk.verb}
                rows={bulk.rows}
                note={bulk.note}
                now={bulk.at}
                onCancel={() => setBulk(null)}
                onConfirm={() => void run(bulk.verb, bulk.rows, bulk.note, simulate)}
                onSwitch={(verb) => setBulk({ ...bulk, verb })}
              />
            ) : bulk?.phase === 'running' ? (
              <ReleaseProgress verb={bulk.verb} count={bulk.count} progress={bulk.progress} />
            ) : bulk?.phase === 'done' ? (
              <ReleaseResult
                verb={bulk.verb}
                summary={bulk.summary}
                asked={bulk.asked}
                retryAt={bulk.retryAt}
                busy={bulk.busy}
                practice={practice}
                onRetry={() => void retryFailed()}
                onPostAudit={() => void postAudit()}
              />
            ) : null}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

interface RowHandlers {
  toggleExpand: (id: number) => void;
  checkboxClick: (e: ReactMouseEvent, id: number) => void;
}

interface RowProps {
  row: ActionRow;
  /** Why it can't be released (said, never hidden), or null. */
  held: string | null;
  /** The regulatory classifier's reason, when it is a regulatory Decision. */
  regulatory: string | null;
  /** The last run's outcome for this row, when it didn't go through. */
  mark: string | null;
  now: number;
  expanded: boolean;
  on: RowHandlers;
}

const ReleaseRow = memo(function ReleaseRow({ row: r, held, regulatory, mark, now, expanded, on }: RowProps) {
  const age = ageDays(r, now);
  const late = pastDue([r], now).length > 0;
  return (
    <>
      <Row id={rowKey(r.id)} className={`${styles.row} ${held ? styles.heldRow : ''}`}>
        <Cell className={styles.ck}>
          {held ? null : (
            // Shift-click on the box selects a range too (the row already does).
            <span onClickCapture={(e) => on.checkboxClick(e, r.id)}>
              <Checkbox slot="selection" className={styles.check} aria-label={`Select ACT-${r.id}`}>
                {({ isSelected, isDisabled }) => <Box on={isSelected} off={isDisabled} />}
              </Checkbox>
            </span>
          )}
        </Cell>
        <Cell className={styles.mono}>ACT-{r.id}</Cell>
        <Cell>
          {r.title || 'Untitled'}
          {regulatory ? (
            <span className={styles.regBadge} title={`Regulatory decision (${regulatory}). Oversight decides it through the regulatory panel.`}>
              Regulatory decision
            </span>
          ) : null}
          {held ? <span className={styles.heldTag}>{held}</span> : null}
          {mark ? (
            <span className={styles.markTag} role="status">
              {mark}
            </span>
          ) : null}
        </Cell>
        <Cell>{r.kind}</Cell>
        <Cell>
          {r.source}
          {r.sourceRef ? <div className={`${styles.sub} ${styles.mono}`}>{r.sourceRef}</div> : null}
        </Cell>
        <Cell>{ROLE_LABEL[r.ownerRole] ?? r.ownerRole}</Cell>
        <Cell className={`${styles.mono} ${late ? styles.old : ''}`}>
          {r.dueDate ? <span title={late ? 'Past due' : undefined}>{fmt(DUE, r.dueDate)}</span> : <span className={styles.none}>None</span>}
        </Cell>
        <Cell className={`${styles.num} ${age !== null && age > OLD_DAYS ? styles.old : ''}`}>
          {age === null ? 'unknown' : age === 0 ? 'today' : plural(age, 'day')}
        </Cell>
        <Cell>{r.executor === 'Agent' ? 'Agent' : 'Person'}</Cell>
        <Cell className={styles.acts}>
          <Button
            className={styles.chev}
            aria-label={`${expanded ? 'Hide' : 'Show'} the details of ACT-${r.id}`}
            aria-expanded={expanded}
            onPress={() => on.toggleExpand(r.id)}
          >
            <ChevronIcon open={expanded} />
          </Button>
        </Cell>
      </Row>
      {expanded ? (
        <Row id={detailKey(r.id)} className={styles.detailRow}>
          <Cell className={styles.ck}>{null}</Cell>
          <Cell colSpan={9}>
            <Details row={r} />
          </Cell>
        </Row>
      ) : null}
    </>
  );
});

function Details({ row }: { row: ActionRow }) {
  return (
    <div className={styles.details}>
      <div>
        <h3 className={styles.detailHead}>What it asks</h3>
        <p className={styles.prose}>{row.description || 'No description.'}</p>
        {row.kind === 'Decision' ? (
          <>
            <h3 className={styles.detailHead}>Options</h3>
            <p className={styles.prose}>{row.options || 'No options recorded.'}</p>
          </>
        ) : null}
      </div>
      <dl className={styles.facts}>
        <dt>Parent</dt>
        <dd>
          {row.parentType ?? 'None'}
          {row.parentId ? ` ${row.parentId}` : ''}
        </dd>
        <dt>Action type</dt>
        <dd className={styles.mono}>{row.actionType ?? 'None'}</dd>
        <dt>Card</dt>
        <dd>{row.cardId ?? 'None'}</dd>
        <dt>Created</dt>
        <dd>{fmt(LONG, row.created) || 'Unknown'}</dd>
        <dt>Due</dt>
        <dd>{fmt(LONG, row.dueDate) || 'None'}</dd>
        <dt>Status</dt>
        <dd>{row.status}</dd>
        {row.releaseNote ? (
          <>
            <dt>Note</dt>
            <dd>{row.releaseNote}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

interface BarProps {
  count: number;
  late: number;
  disabled: boolean;
  onClear: () => void;
  onRelease: (note: string) => void;
  onRetire: (note: string) => void;
}

/**
 * The sticky action bar. It holds the note itself, so typing re-renders only the bar, and stays
 * mounted (hidden) with no selection so the note survives a change of selection.
 */
function ActionBar({ count, late, disabled, onClear, onRelease, onRetire }: BarProps) {
  const [note, setNote] = useState('');
  const ready = count > 0 && note.trim() !== '' && !disabled;
  return (
    <div className={styles.composer} role="region" aria-label="Release or retire the selection" hidden={count === 0}>
      <div className={styles.cnt}>
        {count}
        <small>selected</small>
      </div>
      <div className={styles.noteField}>
        <label htmlFor="release-note" className={styles.sr}>
          Shared note
        </label>
        <textarea
          id="release-note"
          className={styles.noteInput}
          value={note}
          placeholder="This note goes to the owner in their card, word for word (required)"
          aria-describedby="release-note-hint"
          onChange={(e) => setNote(e.target.value)}
        />
        <p id="release-note-hint" className={styles.noteHint}>
          One note for the whole selection.{late ? ` ${late} of these ${late === 1 ? 'is' : 'are'} past due.` : ''}{' '}
          <button type="button" className={styles.linkbtn} onClick={onClear}>
            Clear the selection
          </button>
        </p>
      </div>
      <div className={styles.btns}>
        <Button className={`${styles.btn} ${styles.line}`} isDisabled={!ready} onPress={() => onRetire(note)}>
          Retire {count}
        </Button>
        <Button className={`${styles.btn} ${styles.go}`} isDisabled={!ready} onPress={() => onRelease(note)}>
          Release {count}
        </Button>
      </div>
    </div>
  );
}

function Box({ on, mixed = false, off = false }: { on: boolean; mixed?: boolean; off?: boolean }) {
  return (
    <span className={styles.box} data-on={on || mixed || undefined} data-off={off || undefined} aria-hidden="true">
      {mixed && !on ? (
        <svg viewBox="0 0 16 16">
          <path d="M4 8h8" />
        </svg>
      ) : on ? (
        <svg viewBox="0 0 16 16">
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      ) : null}
    </span>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={open ? styles.chevOpen : undefined}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
